import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUser } from '../api';
import './Admin.css';

const TERMS_PAGE_SIZE = 20;

// Должен совпадать с whitelist на бэке (src/utils/partnerViewers.js).
// При смене править оба места или прокидывать через /users/me.
const PARTNER_VIEWER_EMAILS = ['mayfl@yandex.ru', 'germanx32@gmail.com'];

export default function Admin() {
  const navigate = useNavigate();
  const user = getUser();
  const canViewPartners = !!user && PARTNER_VIEWER_EMAILS.includes((user.email || '').toLowerCase());
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  const [terms, setTerms] = useState([]);
  const [termsTotal, setTermsTotal] = useState(0);
  const [termSearch, setTermSearch] = useState('');
  const [termLang, setTermLang] = useState('ru');
  const [termPage, setTermPage] = useState(0);
  const [newTerm, setNewTerm] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);

  const [assignModal, setAssignModal] = useState(null);
  const [doctorsList, setDoctorsList] = useState([]);
  const [groupModal, setGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Группы чатов (фолдеры)
  const [chatGroups, setChatGroups] = useState([]);
  const [cgLoading, setCgLoading] = useState(false);
  const [cgCreateOpen, setCgCreateOpen] = useState(false);
  const [cgNewName, setCgNewName] = useState('');
  const [cgEditingId, setCgEditingId] = useState(null);
  const [cgEditingName, setCgEditingName] = useState('');
  const [cgRoomsModal, setCgRoomsModal] = useState(null); // { id, name, room_ids }
  const [cgAllRooms, setCgAllRooms] = useState([]);
  const [cgRoomsSearch, setCgRoomsSearch] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      const res = await api.get('/users?' + params);
      setUsers(res.data.users);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, roleFilter]);

  useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, loadUsers]);

  const loadActions = useCallback(async () => {
    try {
      const res = await api.get('/admin/actions?limit=100');
      setActions(res.data.actions);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (tab === 'actions') loadActions(); }, [tab, loadActions]);

  const loadPartners = useCallback(async () => {
    setPartnersLoading(true);
    try {
      const res = await api.get('/partners');
      setPartners(res.data.partners);
    } catch (e) { console.error(e); }
    setPartnersLoading(false);
  }, []);

  useEffect(() => { if (tab === 'partners' && canViewPartners) loadPartners(); }, [tab, canViewPartners, loadPartners]);

  const loadTerms = useCallback(async () => {
    setTermsLoading(true);
    try {
      const params = new URLSearchParams();
      if (termSearch) params.set('search', termSearch);
      if (termLang) params.set('lang', termLang);
      params.set('limit', String(TERMS_PAGE_SIZE));
      params.set('offset', String(termPage * TERMS_PAGE_SIZE));
      const res = await api.get('/med-terms/admin?' + params);
      setTerms(res.data.terms);
      setTermsTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    setTermsLoading(false);
  }, [termSearch, termLang, termPage]);

  useEffect(() => { if (tab === 'terms') loadTerms(); }, [tab, loadTerms]);

  const loadChatGroups = useCallback(async () => {
    setCgLoading(true);
    try {
      const res = await api.get('/chat-groups/admin/all');
      setChatGroups(res.data.groups);
    } catch (e) { console.error(e); }
    setCgLoading(false);
  }, []);

  useEffect(() => { if (tab === 'chat-groups') loadChatGroups(); }, [tab, loadChatGroups]);

  const loadAllRoomsForPicker = useCallback(async () => {
    try {
      const res = await api.get('/rooms');
      setCgAllRooms(res.data.rooms || []);
    } catch (e) { console.error(e); }
  }, []);

  const createChatGroup = async () => {
    const name = cgNewName.trim();
    if (!name) return;
    try {
      await api.post('/chat-groups', { name });
      setCgNewName('');
      setCgCreateOpen(false);
      loadChatGroups();
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const renameChatGroup = async (id) => {
    const name = cgEditingName.trim();
    if (!name) return;
    try {
      await api.patch(`/chat-groups/${id}`, { name });
      setCgEditingId(null);
      setCgEditingName('');
      loadChatGroups();
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const deleteChatGroup = async (g) => {
    if (!window.confirm(`Удалить группу «${g.name}»? Чаты останутся, только пропадёт группировка.`)) return;
    try {
      await api.delete(`/chat-groups/${g.id}`);
      loadChatGroups();
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const uploadGroupAvatar = async (groupId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Файл больше 5 МБ'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/chat-groups/${groupId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadChatGroups();
    } catch (e) { alert('Ошибка загрузки аватара: ' + (e.response?.data?.message || e.message)); }
  };

  const deleteGroupAvatar = async (groupId) => {
    if (!window.confirm('Удалить аватар группы?')) return;
    try {
      await api.delete(`/chat-groups/${groupId}/avatar`);
      loadChatGroups();
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const openRoomsModal = async (g) => {
    await loadAllRoomsForPicker();
    setCgRoomsSearch('');
    setCgRoomsModal(g);
  };

  const addRoomToGroup = async (groupId, roomId) => {
    try {
      await api.post(`/chat-groups/${groupId}/rooms`, { room_id: roomId });
      const fresh = await api.get('/chat-groups/admin/all');
      setChatGroups(fresh.data.groups);
      const updated = fresh.data.groups.find((g) => g.id === groupId);
      if (updated) setCgRoomsModal(updated);
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const removeRoomFromGroup = async (groupId, roomId) => {
    try {
      await api.delete(`/chat-groups/${groupId}/rooms/${roomId}`);
      const fresh = await api.get('/chat-groups/admin/all');
      setChatGroups(fresh.data.groups);
      const updated = fresh.data.groups.find((g) => g.id === groupId);
      if (updated) setCgRoomsModal(updated);
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const roomDisplayName = (r) => {
    if (r.name) return r.name;
    if (r.other_members?.length > 0) return r.other_members.map((m) => m.name).join(', ');
    return '(без названия)';
  };

  const startPartnerChat = async (p) => {
    if (!window.confirm('Создать чат с партнёром ' + p.name + '? Аккаунт будет активирован.')) return;
    try {
      const res = await api.post('/partners/' + p.id + '/start-chat');
      alert('Чат создан. Партнёр активирован.');
      loadPartners();
      // По желанию: переход в чат
      if (res.data.room_id) navigate('/');
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const deletePartner = async (p) => {
    if (!window.confirm('Удалить заявку партнёра ' + p.name + '? Все данные будут удалены безвозвратно.')) return;
    if (!window.confirm('Точно удалить? Действие нельзя отменить.')) return;
    try {
      await api.delete('/partners/' + p.id);
      loadPartners();
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const addMedTerm = async () => {
    const t = newTerm.trim();
    if (!t) return;
    try {
      await api.post('/med-terms', { term: t, lang: termLang });
      setNewTerm('');
      loadTerms();
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const bulkAddMedTerms = async () => {
    const list = bulkText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return;
    if (!window.confirm('Добавить ' + list.length + ' терминов?')) return;
    try {
      const res = await api.post('/med-terms/bulk', { terms: list, lang: termLang });
      alert('Добавлено: ' + res.data.added + ', пропущено: ' + res.data.skipped);
      setBulkText('');
      loadTerms();
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const deleteMedTerm = async (t) => {
    if (!window.confirm('Удалить термин «' + t.term + '»?')) return;
    try {
      await api.delete('/med-terms/' + t.id);
      // Если удалили последний на странице и это не первая — откатиться на стр. назад
      if (terms.length === 1 && termPage > 0) setTermPage(termPage - 1);
      else loadTerms();
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const deleteUserAction = async (u) => {
    if (!window.confirm('Удалить пользователя ' + u.name + '? Все данные будут удалены безвозвратно!')) return;
    if (!window.confirm('Вы уверены? Это действие нельзя отменить.')) return;
    try {
      await api.delete('/users/' + u.id);
      loadUsers();
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка удаления');
    }
  };

  const toggleBlock = async (u, reload = loadUsers) => {
    if (!window.confirm(u.is_blocked ? 'Разблокировать ' + u.name + '?' : 'Заблокировать ' + u.name + '?')) return;
    await api.patch('/users/' + u.id, { is_blocked: !u.is_blocked });
    reload();
  };

  const changeRole = async (u, newRole, reload = loadUsers) => {
    await api.patch('/users/' + u.id, { role: newRole });
    reload();
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return alert('Минимум 6 символов');
    await api.patch('/users/' + resetModal.id, { new_password: newPassword });
    const reload = resetModal._reload || loadUsers;
    setResetModal(null);
    setNewPassword('');
    reload();
    alert('Пароль сброшен');
  };

  const openAssignDoctor = async (client) => {
    const res = await api.get('/users?role=doctor');
    setDoctorsList(res.data.users);
    setAssignModal(client);
  };

  const assignDoctor = async (doctorId) => {
    await api.post('/rooms', { type: 'direct', member_ids: [doctorId] });
    setAssignModal(null);
    alert('Чат создан');
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return alert('Введите название');
    await api.post('/rooms', { type: 'group', name: groupName, member_ids: groupMembers });
    setGroupModal(false);
    setGroupName('');
    setGroupMembers([]);
    alert('Группа создана');
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  const actionLabels = {
    block_user: 'Блокировка',
    unblock_user: 'Разблокировка',
    reset_password: 'Сброс пароля',
    change_role: 'Смена роли',
    create_room: 'Создание чата',
    update_user: 'Обновление',
    delete_user: 'Удаление пользователя',
    partner_chat_created: 'Чат с партнёром создан',
    partner_deleted: 'Партнёр удалён',
    chat_group_created: 'Группа чатов создана',
    chat_group_updated: 'Группа чатов изменена',
    chat_group_deleted: 'Группа чатов удалена',
    chat_group_room_added: 'Чат добавлен в группу',
    chat_group_room_removed: 'Чат убран из группы',
    chat_group_avatar_uploaded: 'Аватар группы обновлён',
    chat_group_avatar_deleted: 'Аватар группы удалён',
    user_avatar_uploaded: 'Аватар пользователя обновлён',
    user_avatar_deleted: 'Аватар пользователя удалён',
    room_avatar_uploaded: 'Аватар чата обновлён',
    room_avatar_deleted: 'Аватар чата удалён',
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Админ-панель</h1>
        <button className="admin-back" onClick={() => navigate('/')}>&#8592; Назад в чат</button>
      </div>

      <div className="admin-tabs">
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>Пользователи</button>
        {canViewPartners && (
          <button className={tab === 'partners' ? 'active' : ''} onClick={() => setTab('partners')}>Партнёры</button>
        )}
        <button className={tab === 'terms' ? 'active' : ''} onClick={() => setTab('terms')}>Словарь</button>
        <button className={tab === 'chat-groups' ? 'active' : ''} onClick={() => setTab('chat-groups')}>Группы чатов</button>
        <button className={tab === 'actions' ? 'active' : ''} onClick={() => setTab('actions')}>Лог действий</button>
      </div>

      {tab === 'users' && (
        <div className="admin-section">
          <div className="admin-filters">
            <input placeholder="Поиск по ФИО или email..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Все роли</option>
              <option value="client">Клиенты</option>
              <option value="doctor">Врачи</option>
              <option value="assistant">Ассистенты</option>
              <option value="admin">Админы</option>
            </select>
            <button className="admin-btn" onClick={() => setGroupModal(true)}>+ Группа</button>
          </div>

          {loading ? <p className="admin-loading">Загрузка...</p> : (
            <table className="admin-table">
              <thead>
                <tr><th>ФИО</th><th>Email</th><th>Роль</th><th>Статус</th><th>Регистрация</th><th>Действия</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.is_blocked ? 'blocked' : ''}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === user.id}>
                        <option value="client">Клиент</option>
                        <option value="doctor">Врач</option>
                        <option value="assistant">Ассистент</option>
                        <option value="admin">Админ</option>
                        <option value="partner">Партнёр</option>
                      </select>
                    </td>
                    <td>{u.is_blocked ? 'Заблокирован' : 'Активен'}</td>
                    <td>{fmt(u.created_at)}</td>
                    <td className="admin-actions-cell">
                      <button onClick={() => toggleBlock(u)}>{u.is_blocked ? 'Разблок' : 'Блок'}</button>
                      <button onClick={() => setResetModal(u)}>Пароль</button>
                      {u.role === 'client' && <button onClick={() => openAssignDoctor(u)}>Врач</button>}
                      {u.id !== user.id && <button onClick={() => deleteUserAction(u)} style={{color:'#AD182C'}}>Удалить</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'partners' && canViewPartners && (
        <div className="admin-section">
          <p className="admin-hint">Заявки от врачей-партнёров. После «Создать чат» аккаунт активируется и появится в списке ваших чатов.</p>
          {partnersLoading ? <p className="admin-loading">Загрузка...</p> : (
            <table className="admin-table">
              <thead>
                <tr><th>ФИО</th><th>Email</th><th>Клиника</th><th>Роль</th><th>Статус</th><th>Дата</th><th>Действия</th></tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id} className={p.is_blocked ? 'blocked' : ''}>
                    <td>{p.name}</td>
                    <td>{p.email}</td>
                    <td>{p.clinic_name || '\u2014'}</td>
                    <td>
                      <select defaultValue="partner" onChange={(e) => changeRole(p, e.target.value, loadPartners)}>
                        <option value="client">Клиент</option>
                        <option value="doctor">Врач</option>
                        <option value="assistant">Ассистент</option>
                        <option value="admin">Админ</option>
                        <option value="partner">Партнёр</option>
                      </select>
                    </td>
                    <td>{p.is_blocked ? 'Новая' : 'Активирован'}</td>
                    <td>{fmt(p.created_at)}</td>
                    <td className="admin-actions-cell">
                      <button onClick={() => startPartnerChat(p)}>Создать чат</button>
                      <button onClick={() => toggleBlock(p, loadPartners)}>{p.is_blocked ? 'Разблок' : 'Блок'}</button>
                      <button onClick={() => setResetModal({ ...p, _reload: loadPartners })}>Пароль</button>
                      <button onClick={() => deletePartner(p)} style={{color:'#AD182C'}}>Удалить</button>
                    </td>
                  </tr>
                ))}
                {partners.length === 0 && <tr><td colSpan="7" style={{ textAlign: 'center', color: '#818C9D' }}>Заявок нет</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'terms' && (
        <div className="admin-section">
          <p className="admin-hint">Словарь медтерминов для проверки орфографии. Слова из этого списка не подчёркиваются как ошибки.</p>
          <div className="admin-filters">
            <input placeholder="Поиск термина..." value={termSearch} onChange={(e) => { setTermSearch(e.target.value); setTermPage(0); }} />
            <select value={termLang} onChange={(e) => { setTermLang(e.target.value); setTermPage(0); }}>
              <option value="ru">Русский</option>
              <option value="en">English / латынь</option>
            </select>
            <span style={{ alignSelf: 'center', color: '#6A6F83' }}>Всего: {termsTotal}</span>
          </div>

          <div className="admin-term-add">
            <input placeholder="Новый термин" value={newTerm} onChange={(e) => setNewTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addMedTerm(); }} />
            <button className="admin-btn" onClick={addMedTerm}>Добавить</button>
          </div>

          <details className="admin-term-bulk">
            <summary>Массовое добавление (по строкам, запятым или ;)</summary>
            <textarea
              rows={5}
              placeholder="ампициллин&#10;цефазолин&#10;диктиокаулёз"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
            />
            <button className="admin-btn" onClick={bulkAddMedTerms}>Импортировать</button>
          </details>

          {termsLoading ? <p className="admin-loading">Загрузка...</p> : (
            <table className="admin-table">
              <thead>
                <tr><th>Термин</th><th>Язык</th><th>Добавлен</th><th></th></tr>
              </thead>
              <tbody>
                {terms.map((t) => (
                  <tr key={t.id}>
                    <td>{t.term}</td>
                    <td>{t.lang}</td>
                    <td>{fmt(t.created_at)}</td>
                    <td className="admin-actions-cell">
                      <button onClick={() => deleteMedTerm(t)} style={{color:'#AD182C'}}>Удалить</button>
                    </td>
                  </tr>
                ))}
                {terms.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', color: '#818C9D' }}>Нет терминов</td></tr>}
              </tbody>
            </table>
          )}

          {termsTotal > TERMS_PAGE_SIZE && (
            <div className="admin-pagination">
              <button className="admin-btn" disabled={termPage === 0} onClick={() => setTermPage(termPage - 1)}>‹ Назад</button>
              <span>Страница {termPage + 1} из {Math.max(1, Math.ceil(termsTotal / TERMS_PAGE_SIZE))}</span>
              <button className="admin-btn" disabled={(termPage + 1) * TERMS_PAGE_SIZE >= termsTotal} onClick={() => setTermPage(termPage + 1)}>Вперёд ›</button>
            </div>
          )}
        </div>
      )}

      {tab === 'chat-groups' && (
        <div className="admin-section">
          <p className="admin-hint">Группы чатов — визуальные папки в списке чатов. Чаты не объединяются. Пользователь видит группу, если состоит хотя бы в одном её чате, и внутри видит только свои чаты из группы.</p>

          <div className="admin-term-add">
            {cgCreateOpen ? (
              <>
                <input placeholder="Название группы" value={cgNewName} onChange={(e) => setCgNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') createChatGroup(); }} autoFocus />
                <button className="admin-btn primary" onClick={createChatGroup}>Создать</button>
                <button className="admin-btn" onClick={() => { setCgCreateOpen(false); setCgNewName(''); }}>Отмена</button>
              </>
            ) : (
              <button className="admin-btn primary" onClick={() => setCgCreateOpen(true)}>+ Создать группу</button>
            )}
          </div>

          {cgLoading ? <p className="admin-loading">Загрузка...</p> : (
            <div className="chat-group-list">
              {chatGroups.length === 0 && <p style={{ color: '#818C9D' }}>Групп пока нет.</p>}
              {chatGroups.map((g) => (
                <div key={g.id} className="chat-group-card">
                  <div className="chat-group-card__avatar">
                    {g.avatar_url
                      ? <img src={g.avatar_url} alt={g.name} />
                      : <div className="chat-group-card__avatar-placeholder">{(g.name || '?').slice(0, 1).toUpperCase()}</div>}
                  </div>
                  <div className="chat-group-card__body">
                    {cgEditingId === g.id ? (
                      <div className="chat-group-card__rename">
                        <input value={cgEditingName} onChange={(e) => setCgEditingName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') renameChatGroup(g.id); }} autoFocus />
                        <button className="admin-btn primary" onClick={() => renameChatGroup(g.id)}>OK</button>
                        <button className="admin-btn" onClick={() => { setCgEditingId(null); setCgEditingName(''); }}>Отмена</button>
                      </div>
                    ) : (
                      <div className="chat-group-card__title">
                        <b>{g.name}</b>
                        <span className="chat-group-card__count">{g.room_ids.length} {g.room_ids.length === 1 ? 'чат' : 'чатов'}</span>
                      </div>
                    )}
                    <div className="chat-group-card__actions">
                      {cgEditingId !== g.id && <button className="admin-btn" onClick={() => { setCgEditingId(g.id); setCgEditingName(g.name); }}>Переименовать</button>}
                      <button className="admin-btn" onClick={() => openRoomsModal(g)}>Состав</button>
                      <label className="admin-btn" style={{ cursor: 'pointer', margin: 0 }}>
                        {g.avatar_url ? 'Сменить аватар' : 'Загрузить аватар'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={(e) => { uploadGroupAvatar(g.id, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                      {g.avatar_url && <button className="admin-btn" onClick={() => deleteGroupAvatar(g.id)}>Убрать аватар</button>}
                      <button className="admin-btn" onClick={() => deleteChatGroup(g)} style={{ color: '#AD182C' }}>Удалить</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'actions' && (
        <div className="admin-section">
          <table className="admin-table">
            <thead>
              <tr><th>Дата</th><th>Админ</th><th>Действие</th><th>Цель</th><th>Детали</th></tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a.id}>
                  <td>{fmt(a.created_at)}</td>
                  <td>{a.admin_name}</td>
                  <td>{actionLabels[a.action] || a.action}</td>
                  <td>{a.target_name || a.target_email || '\u2014'}</td>
                  <td className="admin-details">{a.details ? JSON.stringify(a.details) : '\u2014'}</td>
                </tr>
              ))}
              {actions.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', color: '#818C9D' }}>Нет записей</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Назначить врача для {assignModal.name}</h3>
            {doctorsList.map((d) => (
              <button key={d.id} className="modal-option" onClick={() => assignDoctor(d.id)}>{d.name} ({d.email})</button>
            ))}
            {doctorsList.length === 0 && <p>Нет врачей</p>}
            <button className="modal-close" onClick={() => setAssignModal(null)}>Отмена</button>
          </div>
        </div>
      )}

      {resetModal && (
        <div className="modal-overlay" onClick={() => setResetModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Сброс пароля: {resetModal.name}</h3>
            <input type="text" placeholder="Новый пароль (мин. 6 символов)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus />
            <div className="modal-buttons">
              <button className="admin-btn primary" onClick={handleResetPassword}>Сбросить</button>
              <button className="modal-close" onClick={() => { setResetModal(null); setNewPassword(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {cgRoomsModal && (
        <div className="modal-overlay" onClick={() => setCgRoomsModal(null)}>
          <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
            <h3>Состав группы: {cgRoomsModal.name}</h3>
            <input placeholder="Поиск чата..." value={cgRoomsSearch} onChange={(e) => setCgRoomsSearch(e.target.value)} style={{ marginBottom: 12 }} />
            <p className="modal-label">В группе ({cgRoomsModal.room_ids.length}):</p>
            <div className="modal-members">
              {cgRoomsModal.room_ids.length === 0 && <p style={{ color: '#818C9D', margin: '6px 0' }}>Пусто — добавьте чаты из списка ниже.</p>}
              {cgAllRooms
                .filter((r) => cgRoomsModal.room_ids.includes(r.id))
                .filter((r) => !cgRoomsSearch || roomDisplayName(r).toLowerCase().includes(cgRoomsSearch.toLowerCase()))
                .map((r) => (
                  <div key={r.id} className="modal-member modal-member--row">
                    <span>{roomDisplayName(r)}</span>
                    <button className="admin-btn" onClick={() => removeRoomFromGroup(cgRoomsModal.id, r.id)}>Убрать</button>
                  </div>
                ))}
            </div>
            <p className="modal-label">Доступные чаты:</p>
            <div className="modal-members">
              {cgAllRooms
                .filter((r) => !cgRoomsModal.room_ids.includes(r.id))
                .filter((r) => !cgRoomsSearch || roomDisplayName(r).toLowerCase().includes(cgRoomsSearch.toLowerCase()))
                .slice(0, 200)
                .map((r) => (
                  <div key={r.id} className="modal-member modal-member--row">
                    <span>{roomDisplayName(r)}</span>
                    <button className="admin-btn primary" onClick={() => addRoomToGroup(cgRoomsModal.id, r.id)}>Добавить</button>
                  </div>
                ))}
            </div>
            <div className="modal-buttons">
              <button className="modal-close" onClick={() => setCgRoomsModal(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {groupModal && (
        <div className="modal-overlay" onClick={() => setGroupModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Создать группу</h3>
            <input placeholder="Название группы" value={groupName} onChange={(e) => setGroupName(e.target.value)} autoFocus />
            <p className="modal-label">Участники:</p>
            <div className="modal-members">
              {users.filter((u) => u.id !== user.id).map((u) => (
                <label key={u.id} className="modal-member">
                  <input type="checkbox" checked={groupMembers.includes(u.id)} onChange={(e) => {
                    if (e.target.checked) setGroupMembers([...groupMembers, u.id]);
                    else setGroupMembers(groupMembers.filter((id) => id !== u.id));
                  }} />
                  {u.name} ({u.role})
                </label>
              ))}
            </div>
            <div className="modal-buttons">
              <button className="admin-btn primary" onClick={handleCreateGroup}>Создать</button>
              <button className="modal-close" onClick={() => setGroupModal(false)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
